import {FC, useEffect, useMemo, useRef, useState} from 'react'
import JsBarcode from 'jsbarcode'
import {useParams} from 'react-router-dom'
import {Loading} from '../../../components/Loading/Loading'

type BarcodeItem = {
  sku: string
  label?: string
  url?: string
}

type BarcodePayloadType = {
  type?: 'batch'
  target?: 'retail' | 'wholesale'
  sku?: string
  label?: string
  url?: string
  items?: BarcodeItem[]
}

const BarcodeItemRenderer: FC<{item: BarcodeItem; target?: string}> = ({item, target}) => {
  const barcodeRef = useRef<SVGSVGElement | null>(null)
  const sku = item.sku.trim()

  // Validação simples para EAN-13
  const isEan13 = useMemo(() => /^\d{13}$/.test(sku), [sku])

  useEffect(() => {
    if (!barcodeRef.current || !sku) return

    try {
      JsBarcode(barcodeRef.current, sku, {
        format: isEan13 ? 'EAN13' : 'CODE128',
        lineColor: '#000',
        width: 2.5,
        height: 120,
        displayValue: true,
        fontSize: 18,
        margin: 15,
        background: '#ffffff',
      })
    } catch (error) {
      console.log('Erro ao gerar código de barras para o SKU:', sku, error)
    }
  }, [sku, isEan13])

  return (
    <div className='d-flex flex-column align-items-center'>
      <div
        style={{
          border: '1px dashed #CCC',
          padding: 24,
          background: '#fff',
          display: 'inline-block',
        }}
      >
        <svg ref={barcodeRef} />
      </div>

      <div className='text-center mt-3' style={{maxWidth: '100%'}}>
        <div className='fw-bold fs-5 text-gray-800' style={{whiteSpace: 'pre-line'}}>
          {item.label || 'Código de Barras da Variação'}
        </div>
        <div className='text-muted fs-7'>SKU: {sku}</div>
        <div className='text-muted fs-7'>Formato: {isEan13 ? 'EAN13' : 'CODE128'}</div>

        {item.url && (
          <div
            className='no-print'
            style={{textAlign: 'center', fontFamily: 'Arial', fontSize: 12, paddingTop: 10}}
          >
            <a href={item.url} target='_blank' rel='noreferrer' className='text-primary fw-bold'>
              Acessar no WebApp {target === 'wholesale' ? '(Atacado)' : ''}
            </a>
          </div>
        )}
      </div>
    </div>
  )
}

const GenerateBarcodeContent: FC = () => {
  const {data} = useParams()
  const [loading, setLoading] = useState(false)
  const [payload, setPayload] = useState<BarcodePayloadType | null>(null)

  const decodePayload = (param?: string) => {
    if (!param) return null

    const decodedParam = decodeURIComponent(param)
    let payloadBase64 = ''

    if (decodedParam.startsWith('barcode-cache-')) {
      const cachedPayload = localStorage.getItem(decodedParam)
      if (!cachedPayload) {
        throw new Error('Payload do código de barras não encontrado no cache local')
      }
      payloadBase64 = cachedPayload
      localStorage.removeItem(decodedParam)
    } else {
      payloadBase64 = decodedParam
    }

    return JSON.parse(atob(payloadBase64)) as BarcodePayloadType
  }

  useEffect(() => {
    if (!data) return

    try {
      setLoading(true)
      const parsedPayload = decodePayload(data)
      setPayload(parsedPayload)
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }, [data])

  const itemsToRender = useMemo(() => {
    if (!payload) return []

    if (payload.type === 'batch' && payload.items && payload.items.length > 0) {
      return payload.items
    }

    if (payload.sku) {
      return [
        {
          sku: payload.sku,
          label: payload.label,
          url: payload.url,
        },
      ]
    }

    return []
  }, [payload])

  return (
    <>
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
            body {
              background-color: white;
            }
          }
        `}
      </style>

      {loading ? (
        <Loading />
      ) : (
        <div style={{margin: 8}}>
          <div className='w-100 no-print'>
            <div
              className='d-flex justify-content-center align-items-center'
              style={{
                height: '5vh',
                background: '#F00',
                marginBottom: '1rem',
              }}
            >
              <span className='text-white fs-4 text-center'>
                Recomendamos que faça um teste nos códigos de barras gerados antes de imprimir em
                escala
              </span>
            </div>
          </div>

          <div className='d-flex flex-row flex-wrap align-items-start justify-content-start gap-4 p-3'>
            {itemsToRender.map((item, index) => (
              <BarcodeItemRenderer
                key={`barcode-${index}-${item.sku}`}
                item={item}
                target={payload?.target}
              />
            ))}
          </div>
        </div>
      )}
    </>
  )
}

const GenerateBarcodeWebapp: FC = () => {
  return <GenerateBarcodeContent />
}

export {GenerateBarcodeWebapp}
